/* ==========================================================================
   ATN Membership - shared mobile slider
   Turns a CSS grid into a scroll-snap slider below a configurable breakpoint.

   Markup contract:
     [data-atn-mslider]        root (carries data-mslider-bp / data-mslider-loop)
       [data-mslider-track]    the grid element
       [data-mslider-slide]    each item
       [data-mslider-prev]     previous arrow button
       [data-mslider-next]     next arrow button
       [data-mslider-dots]     empty container, dots are injected here
   ========================================================================== */

(function () {
  'use strict';

  if (window.atnMemMSlider) {
    window.atnMemMSlider.refresh();
    return;
  }

  var instances = [];

  function toArray(list) {
    return Array.prototype.slice.call(list);
  }

  function Slider(root) {
    this.root = root;
    this.track = root.querySelector('[data-mslider-track]');
    this.prevBtn = root.querySelector('[data-mslider-prev]');
    this.nextBtn = root.querySelector('[data-mslider-next]');
    this.dotsHost = root.querySelector('[data-mslider-dots]');

    if (!this.track) return;

    this.slides = toArray(this.track.querySelectorAll('[data-mslider-slide]'));
    if (!this.slides.length) return;

    this.loop = root.getAttribute('data-mslider-loop') === 'true';

    var bp = parseInt(root.getAttribute('data-mslider-bp'), 10);
    this.bp = isNaN(bp) ? 749 : bp;

    this.active = 0;
    this.ticking = false;
    this.enabled = false;
    this.dots = [];

    this.onScroll = this.handleScroll.bind(this);
    this.onPrev = this.handlePrev.bind(this);
    this.onNext = this.handleNext.bind(this);
    this.onMediaChange = this.evaluate.bind(this);

    this.mq = window.matchMedia('(max-width: ' + this.bp + 'px)');
    if (this.mq.addEventListener) {
      this.mq.addEventListener('change', this.onMediaChange);
    } else if (this.mq.addListener) {
      this.mq.addListener(this.onMediaChange);
    }

    this.evaluate();
    root.setAttribute('data-mslider-ready', 'true');
  }

  Slider.prototype.evaluate = function () {
    if (this.mq.matches) {
      this.enable();
    } else {
      this.disable();
    }
  };

  Slider.prototype.enable = function () {
    if (this.enabled) {
      this.buildDots();
      this.updateUi();
      return;
    }
    this.enabled = true;
    this.root.classList.add('is-mslider');

    this.track.addEventListener('scroll', this.onScroll, { passive: true });
    if (this.prevBtn) this.prevBtn.addEventListener('click', this.onPrev);
    if (this.nextBtn) this.nextBtn.addEventListener('click', this.onNext);

    this.buildDots();
    this.updateUi();
  };

  Slider.prototype.disable = function () {
    if (!this.enabled) return;
    this.enabled = false;
    this.root.classList.remove('is-mslider');

    this.track.removeEventListener('scroll', this.onScroll);
    if (this.prevBtn) this.prevBtn.removeEventListener('click', this.onPrev);
    if (this.nextBtn) this.nextBtn.removeEventListener('click', this.onNext);

    this.clearDots();
    this.track.scrollLeft = 0;
    this.active = 0;
  };

  Slider.prototype.clearDots = function () {
    if (!this.dotsHost) return;
    while (this.dotsHost.firstChild) {
      this.dotsHost.removeChild(this.dotsHost.firstChild);
    }
    this.dots = [];
  };

  Slider.prototype.buildDots = function () {
    if (!this.dotsHost) return;
    this.clearDots();

    var self = this;
    for (var i = 0; i < this.slides.length; i++) {
      var dot = document.createElement('button');
      dot.type = 'button';
      dot.className = 'atn-ms-dot';
      dot.setAttribute('aria-label', 'Go to slide ' + (i + 1));
      (function (index) {
        dot.addEventListener('click', function () {
          self.goTo(index);
        });
      })(i);
      this.dotsHost.appendChild(dot);
      this.dots.push(dot);
    }
  };

  Slider.prototype.goTo = function (index) {
    var last = this.slides.length - 1;

    if (index < 0) index = this.loop ? last : 0;
    if (index > last) index = this.loop ? 0 : last;

    var slide = this.slides[index];
    if (!slide) return;

    var left = slide.offsetLeft - this.track.offsetLeft;
    if (this.track.scrollTo) {
      this.track.scrollTo({ left: left, behavior: 'smooth' });
    } else {
      this.track.scrollLeft = left;
    }
    this.active = index;
    this.updateUi();
  };

  Slider.prototype.handlePrev = function () {
    this.goTo(this.active - 1);
  };

  Slider.prototype.handleNext = function () {
    this.goTo(this.active + 1);
  };

  Slider.prototype.handleScroll = function () {
    if (this.ticking) return;
    this.ticking = true;

    var self = this;
    window.requestAnimationFrame(function () {
      self.ticking = false;
      self.readActive();
      self.updateUi();
    });
  };

  Slider.prototype.readActive = function () {
    var pos = this.track.scrollLeft;
    var best = 0;
    var bestDist = Infinity;

    for (var i = 0; i < this.slides.length; i++) {
      var left = this.slides[i].offsetLeft - this.track.offsetLeft;
      var dist = Math.abs(left - pos);
      if (dist < bestDist) {
        bestDist = dist;
        best = i;
      }
    }
    this.active = best;
  };

  Slider.prototype.updateUi = function () {
    for (var i = 0; i < this.dots.length; i++) {
      var isActive = i === this.active;
      this.dots[i].classList.toggle('is-active', isActive);
      this.dots[i].setAttribute('aria-current', isActive ? 'true' : 'false');
    }

    if (this.loop) {
      if (this.prevBtn) this.prevBtn.classList.remove('is-disabled');
      if (this.nextBtn) this.nextBtn.classList.remove('is-disabled');
      return;
    }

    var atStart = this.active <= 0;
    var atEnd = this.active >= this.slides.length - 1;

    if (this.prevBtn) this.prevBtn.classList.toggle('is-disabled', atStart);
    if (this.nextBtn) this.nextBtn.classList.toggle('is-disabled', atEnd);
  };

  Slider.prototype.scrollToBlock = function (blockId) {
    if (!this.enabled) return;
    for (var i = 0; i < this.slides.length; i++) {
      if (this.slides[i].getAttribute('data-block-id') === blockId) {
        this.goTo(i);
        return;
      }
    }
  };

  Slider.prototype.destroy = function () {
    this.disable();
    if (this.mq.removeEventListener) {
      this.mq.removeEventListener('change', this.onMediaChange);
    } else if (this.mq.removeListener) {
      this.mq.removeListener(this.onMediaChange);
    }
    this.root.removeAttribute('data-mslider-ready');
  };

  function scan(scope) {
    var context = scope || document;
    var roots = toArray(context.querySelectorAll('[data-atn-mslider]'));

    for (var i = 0; i < roots.length; i++) {
      if (roots[i].getAttribute('data-mslider-ready') === 'true') continue;
      instances.push(new Slider(roots[i]));
    }
  }

  function refresh() {
    for (var i = 0; i < instances.length; i++) {
      if (instances[i] && instances[i].track) instances[i].evaluate();
    }
    scan(document);
  }

  function unload(scope) {
    var kept = [];
    for (var i = 0; i < instances.length; i++) {
      var inst = instances[i];
      if (!inst || !inst.root) continue;
      if (scope && scope.contains(inst.root)) {
        inst.destroy();
      } else {
        kept.push(inst);
      }
    }
    instances = kept;
  }

  window.atnMemMSlider = {
    scan: scan,
    refresh: refresh,
    instances: instances
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      scan(document);
    });
  } else {
    scan(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    scan(event.target);
  });

  document.addEventListener('shopify:section:unload', function (event) {
    unload(event.target);
  });

  document.addEventListener('shopify:block:select', function (event) {
    var blockId = event.target.getAttribute('data-block-id');
    if (!blockId) return;
    for (var i = 0; i < instances.length; i++) {
      if (instances[i] && instances[i].root.contains(event.target)) {
        instances[i].scrollToBlock(blockId);
      }
    }
  });
})();